import { useCallback, useEffect, useState } from "react";
import {
  fetchDashboardSummary,
  getDashboardReportingPeriod,
  localIsoDate,
  parseIsoDate,
} from "../utils/dashboardAppraisalSummary";
import { dashboardMockData } from "../dashboardMockData";

const emptySummary = () =>
  dashboardMockData.summary.map((item) => ({ ...item, value: 0 }));

const emptySchedule = () => ({
  today: localIsoDate(),
  events: [],
  upcoming: [],
});

export function useDashboardPafSummary() {
  const [summaryItems, setSummaryItems] = useState(emptySummary);
  const [reportingPeriod, setReportingPeriod] = useState(
    getDashboardReportingPeriod()
  );
  const [today, setToday] = useState(emptySchedule().today);
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyResult = (result) => {
    setSummaryItems(result.summaryItems);
    setReportingPeriod(
      getDashboardReportingPeriod(parseIsoDate(result.today) ?? new Date())
    );
    setToday(result.today);
    setEvents(result.events);
    setUpcoming(result.upcoming);
  };

  const loadSummary = useCallback(async ({ force = false } = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchDashboardSummary({ force });
      applyResult(result);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load dashboard summary.";
      setSummaryItems(emptySummary());
      setEvents([]);
      setUpcoming([]);
      setError(String(message));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await fetchDashboardSummary();
        if (!cancelled) {
          applyResult(result);
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Failed to load dashboard summary.";
          setSummaryItems(emptySummary());
          setEvents([]);
          setUpcoming([]);
          setError(String(message));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = useCallback(() => loadSummary({ force: true }), [loadSummary]);

  return {
    summaryItems,
    reportingPeriod,
    today,
    events,
    upcoming,
    isLoading,
    error,
    refetch,
  };
}
